// Public route — /dpa. MA-71. Data Processing Agreement summary page.
// District/school-facing: reachable before sign-in via App.jsx public bypass.
// Shows sub-processor list + data-elements summary + links to request a DPA.
// NOT linked from LoginScreen (district-facing, not consumer pre-consent flow).
import { createFileRoute } from "@tanstack/react-router";
import { LegalPage, LegalSection } from "@/components/LegalPage";

const LAST_UPDATED = "June 30, 2026";

const SUB_PROCESSORS = [
  {
    name: "Google LLC",
    role: "OAuth sign-in (identity provider)",
    data: "Parent email address, auth token",
    dpaUrl: "https://workspace.google.com/terms/dpa_terms.html",
  },
  {
    name: "Supabase Inc.",
    role: "Cloud database and authentication",
    data: "All child PII + parent email",
    dpaUrl: "https://supabase.com/legal/dpa",
  },
  {
    name: "Functional Software Inc. (Sentry)",
    role: "Error monitoring",
    data: "Filtered crash traces (child PII stripped before transmission)",
    dpaUrl: "https://sentry.io/legal/dpa/",
  },
  {
    name: "Resend Inc.",
    role: "Transactional email delivery",
    data: "Parent email address only",
    dpaUrl: "https://resend.com/legal/privacy-policy",
  },
  {
    name: "Vercel Inc.",
    role: "Hosting and edge functions",
    data: "Request metadata and hosting logs (no persistent child PII)",
    dpaUrl: "https://vercel.com/legal/dpa",
  },
  {
    name: "Anthropic PBC",
    role: "AI inference (where AI features are active)",
    data: "Session context — exact scope under verification",
    dpaUrl: "https://www.anthropic.com/privacy",
  },
];

function DpaPage() {
  return (
    <LegalPage title="Data Processing Agreement (DPA)" lastUpdated={LAST_UPDATED}>
      <LegalSection heading="For schools and districts">
        <p>
          PencilWise is designed to meet the data privacy requirements schools
          and districts expect when adopting an educational app. This page
          summarises our data practices and sub-processor list.
        </p>
        <p>
          A full DPA template aligned to the SDPC National DPA framework is
          available on request.{" "}
          <a
            className="font-bold underline"
            href="mailto:support@pencilwise.app?subject=DPA%20request"
          >
            Email us at support@pencilwise.app
          </a>{" "}
          to receive a counsel-reviewed version for signature. The template is
          currently in draft — do not sign the draft version.
        </p>
      </LegalSection>

      <LegalSection heading="What data we collect">
        <p>
          From the parent or school account: a Google email address used for
          sign-in. From the child: a first name or nickname and an age band.
          As the child practises, we record activity — questions presented,
          answers given, and skill progress. We never collect birth dates,
          precise location, photos, or biometrics.
        </p>
      </LegalSection>

      <LegalSection heading="How we use it">
        <p>
          We use child data only to operate and personalise the learning
          experience. We never sell student data, use it for advertising, or
          share it with third parties beyond the service providers listed below.
        </p>
      </LegalSection>

      <LegalSection heading="Sub-processors">
        <p>
          These are the companies that process data on PencilWise&apos;s behalf.
          We never sell your data to any of them or allow them to use it for
          their own purposes.
        </p>
        <div className="mt-3 overflow-x-auto">
          <table className="w-full border-collapse text-[13px]">
            <thead>
              <tr className="border-b border-border text-left">
                <th className="py-1.5 pr-3 text-xs font-bold text-muted-foreground">
                  Provider
                </th>
                <th className="py-1.5 pr-3 text-xs font-bold text-muted-foreground">
                  Role
                </th>
                <th className="py-1.5 text-xs font-bold text-muted-foreground">
                  Data shared
                </th>
              </tr>
            </thead>
            <tbody>
              {SUB_PROCESSORS.map((sp) => (
                <tr
                  key={sp.name}
                  className="border-b border-border/50 align-top"
                >
                  <td className="py-2 pr-3 font-semibold">
                    <a
                      href={sp.dpaUrl}
                      target="_blank"
                      rel="noreferrer"
                      className="underline hover:text-foreground"
                    >
                      {sp.name}
                    </a>
                  </td>
                  <td className="py-2 pr-3 text-foreground/80">{sp.role}</td>
                  <td className="py-2 text-foreground/80">{sp.data}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <p className="mt-3 text-[13px] text-foreground/70">
          Full sub-processor inventory with data-flow details and security
          assurances:{" "}
          <code className="rounded bg-muted px-1 text-[12px]">
            docs/legal/sub-processors.md
          </code>{" "}
          in the public repository.
        </p>
      </LegalSection>

      <LegalSection heading="Security">
        <p>
          PencilWise implements reasonable technical and administrative safeguards
          to protect student data, including encrypted data transmission (HTTPS),
          row-level database access controls scoped to each parent account, and
          personal-information filtering on error-monitoring events. A Written
          Information Security Program (WISP) as required by COPPA §312.8 is
          in draft and will be completed before any school pilot.
        </p>
      </LegalSection>

      <LegalSection heading="Data deletion and export">
        <p>
          Parents can export all their data (including child practice records)
          or delete their account and all associated data at any time from
          Settings → Privacy. Schools may request data return or deletion by
          emailing{" "}
          <a
            className="font-bold underline"
            href="mailto:support@pencilwise.app"
          >
            support@pencilwise.app
          </a>
          .
        </p>
      </LegalSection>

      <LegalSection heading="Request a DPA">
        <p>
          If your school or district requires a signed Data Processing Agreement
          before adopting PencilWise, email{" "}
          <a
            className="font-bold underline"
            href="mailto:support@pencilwise.app?subject=DPA%20request"
          >
            support@pencilwise.app
          </a>{" "}
          with the subject line &ldquo;DPA request&rdquo;. We will send you a
          counsel-reviewed template for your review.
        </p>
      </LegalSection>
    </LegalPage>
  );
}

export const Route = createFileRoute("/dpa")({ component: DpaPage });
